import React, { createContext, useContext, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import useUser from 'hooks/useUser';

const WebsiteCurrencyContext = createContext({
  currency: null,
  ready: false,
});

const WEBSITE_CURRENCY_QUERY = gql`
  query WebsiteCurrency($customerId: ID!, $dataSourceIds: [ID!]) {
    dataSources(customerId: $customerId, ids: $dataSourceIds) {
      id
      website {
        id
        matomoId
        matomoSettings {
          currency
        }
      }
    }
  }
`;

function resolveWebsiteCurrency(dataSources) {
  const currencies = [...new Set(
    (dataSources || [])
      .map(ds => ds.website?.matomoSettings?.currency)
      .filter(Boolean),
  )];

  return currencies[0] || null;
}

export function WebsiteCurrencyProvider({ customerId: customerIdProp, dataSourceIds, skip, children }) {
  const { user } = useUser();
  const customerId = customerIdProp || user?.globalSettings?.selectedCustomer;

  const { data, loading } = useQuery(WEBSITE_CURRENCY_QUERY, {
    variables: {
      customerId,
      ...(dataSourceIds?.length ? { dataSourceIds } : {}),
    },
    skip: skip || !customerId,
    fetchPolicy: 'cache-first',
  });

  const value = useMemo(() => {
    if (skip) {
      return { currency: null, ready: true };
    }

    if (loading || !data?.dataSources) {
      return { currency: null, ready: false };
    }

    return {
      currency: resolveWebsiteCurrency(data.dataSources),
      ready: true,
    };
  }, [data, loading, skip]);

  return (
    <WebsiteCurrencyContext.Provider value={value}>
      {children}
    </WebsiteCurrencyContext.Provider>
  );
}

export function useWebsiteCurrency() {
  return useContext(WebsiteCurrencyContext);
}
